import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, History, BarChart3, Flame, Settings as SettingsIcon, User as UserIcon } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

export default function AppShell({ children, profile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const navItems = [
    { to: "/home", label: "Today", icon: Home, testid: "nav-today" },
    { to: "/history", label: "History", icon: History, testid: "nav-history" },
    { to: "/stats", label: "Stats", icon: BarChart3, testid: "nav-stats" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 relative">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5" data-testid="logo-home">
            <Logo size={36} />
            <span className="font-heading font-black tracking-tight text-lg">Fairway Lab</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {navItems.map((it) => {
              const active = location.pathname === it.to;
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                >
                  <span className="inline-flex items-center gap-2"><Icon className="w-4 h-4" /> {it.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            {profile && (
              <div className="chip" data-testid="header-streak">
                <Flame className="w-3 h-3 text-[#0B2545]" />
                <span>{profile.streak_count || 0} day streak</span>
              </div>
            )}
            {user && (
              <Link
                to="/settings"
                data-testid="header-avatar"
                title={user.name}
                className="w-9 h-9 rounded-full bg-slate-100 hover:ring-2 hover:ring-[#0B2545]/30 transition-all flex items-center justify-center overflow-hidden border border-slate-200"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <UserIcon className="w-4 h-4 text-slate-500" />
                )}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-40 sm:pb-32">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-4 left-4 z-40 bg-slate-900 text-white rounded-full shadow-lg px-2 py-2 flex items-center gap-1" data-testid="mobile-bottom-nav">
        {navItems.map((it) => {
          const active = location.pathname === it.to;
          const Icon = it.icon;
          return (
            <button
              key={it.to}
              onClick={() => navigate(it.to)}
              data-testid={`mobile-${it.testid}`}
              className={`px-3 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-2 ${active ? "bg-[#0B2545] text-white" : "text-white/80 hover:text-white"}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{it.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
